/**
 * **通用账户明细抽屉**（批次 `95`）
 *
 * ## 🔴 为什么另做一个，不复用 `ContraTimelineDrawer`
 *
 * 往来账户的时间线里有 **`settlement` 结算行**（本金的唯一痕迹，`DR-047`），
 * 那是往来域独有的语义；预付 / 待摊 / 信用卡没有"结算"这回事。
 * 硬套会让明细里出现一行永远为空的"结算"分类 —— 比没有更糟。
 *
 * ## 口径
 *
 * - 金额取腿的 `amount`（**恒正**）+ `direction`，**服务端不翻符号**；
 * - 分类名由服务端 JOIN 下发（批 `106-A`）—— 端侧**不按 `categoryId` 自己 mapping**（铁律 103）；
 * - 只显示最近 N 条（服务端夹上限并回报 `truncated`），**不静默截断**；
 * - 🆕 **一行 = 银行流水的一笔**（`AC-B2.64`）：服务端已按「同一交易在该账户的同向腿」
 *   合并（`toBillLegRows`）；展开子项只是看"这笔钱怎么分的"，**合计不变**（`AC-B2.39`）。
 */

import type { ReactElement } from 'react';
import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { toCents } from '@jiaying/shared';
import { getAccountLedger } from '../api/endpoints.js';
import { Amount } from './Amount.js';
import { Loading } from './Loading.js';

export interface AccountLedgerDrawerProps {
  /** `null` = 关闭 */
  accountId: string | null;
  accountName: string;
  currency: string;
  onClose: () => void;
}

export function AccountLedgerDrawer({
  accountId,
  accountName,
  currency,
  onClose,
}: AccountLedgerDrawerProps): ReactElement {
  const open = accountId !== null;
  const [openTxn, setOpenTxn] = useState<string | null>(null);
  const ledger = useQuery({
    queryKey: ['accountLedger', accountId ?? ''],
    queryFn: () => getAccountLedger(accountId as string),
    enabled: open,
  });

  return (
    <Drawer anchor="right" open={open} onClose={onClose} data-testid="account-ledger-drawer">
      <Box sx={{ width: { xs: '100vw', sm: 480 }, p: 2.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {accountName} · 明细
        </Typography>
        <Typography variant="caption" color="text.secondary">
          这个账户上每一笔钱进出的记录（含转账的另一端）
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        {ledger.isPending && <Loading py={3} />}
        {ledger.isError && (
          <Alert severity="error">明细读取失败：{String(ledger.error)}</Alert>
        )}
        {ledger.isSuccess && ledger.data.items.length === 0 && (
          <Typography variant="body2" color="text.secondary" data-testid="ledger-empty">
            这个账户还没有任何流水。
          </Typography>
        )}
        {ledger.isSuccess && ledger.data.items.length > 0 && (
          <Stack spacing={1.25} divider={<Divider flexItem />} data-testid="ledger-list">
            {ledger.data.items.map((row) => {
              const merged = row.legCount > 1;
              const expanded = openTxn === `${row.transactionId}|${row.direction}`;
              return (
                <Box key={`${row.transactionId}-${row.direction}-${row.date}`}>
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {/* 🔴 分类名**由服务端下发**（批 106-A）—— 端侧不按 id 自己 mapping（铁律 103） */}
                        {merged && row.categoryId === null
                          ? `拆分 ${String(row.legCount)} 项`
                          : (row.label ?? (row.categoryId === null ? '未分类' : row.categoryId))}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {row.date}
                        {row.note !== null && row.note !== '' ? ` · ${row.note}` : ''}
                        {merged ? ' · 已合并成一笔' : ''}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Amount
                        cents={toCents(row.direction === 'in' ? row.amount : -row.amount)}
                        currency={currency}
                        signed
                        sx={{ fontSize: '0.875rem', fontWeight: 600, flexShrink: 0 }}
                      />
                      {merged && (
                        <Button
                          size="small"
                          sx={{ minWidth: 0, px: 0.5 }}
                          data-testid={`ledger-expand-${row.transactionId}`}
                          onClick={() =>
                            setOpenTxn(expanded ? null : `${row.transactionId}|${row.direction}`)
                          }
                        >
                          {expanded ? '收起' : '展开'}
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                  {/* 🔴 展开**不改变任何合计**（`AC-B2.39`）：子项只回答"这笔钱是怎么分的" */}
                  {expanded && row.children !== undefined && (
                    <Stack spacing={0.5} sx={{ mt: 1, pl: 1.5 }} data-testid="ledger-children">
                      {row.children.map((child, idx) => (
                        <Stack
                          key={`${child.categoryId ?? 'none'}-${String(child.amount)}-${String(idx)}`}
                          direction="row"
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {child.label ?? (child.categoryId === null ? '未分类' : child.categoryId)}
                          </Typography>
                          <Amount
                            cents={toCents(row.direction === 'in' ? child.amount : -child.amount)}
                            currency={currency}
                            signed
                            sx={{ fontSize: '0.75rem' }}
                          />
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}

        {ledger.isSuccess && ledger.data.truncated && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
            只显示最近 {ledger.data.count} 条 —— 更早的请到「流水」页按账户筛选。
          </Typography>
        )}

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button size="small" onClick={onClose}>
            关闭
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}
